/**
 * Theme Definitions
 * The 6 branded themes for SmallBiz Innovation
 *
 * NOTE (2026-06-21 reconcile): the per-state `light`/`dark` core color values
 * below are pinned to the DEPLOYED theme contract (see ./contract.ts and
 * libs/ui/brand-kit/styles/theme.css), NOT to the raw brand-data palette.
 * Ben's decision: deployed values are canonical. Where a palette token happened
 * to equal the deployed hex it is kept symbolic; where the deployed value
 * differed, an explicit hex is used. The full ~30-var contract lives in
 * ./contract.ts; this `themes` export remains the 11-core-field shape the
 * brand-showcase app consumes.
 *
 * FORMAT NOTE (#1169): values here are intentionally kept as HEX, even though
 * `contract.ts` (the theme.css SSOT) was migrated to oklch(). This export is
 * consumed at runtime by hex-only brand tooling (e.g. the showcase's
 * hexToRgb / relativeLuminance WCAG calculators under each app's
 * brand/colors/themes.ts), which throw on non-hex input. Same colors, different
 * notation — do NOT "sync" these to oklch without first teaching those
 * consumers to parse oklch().
 *
 * #3071 S2: each theme's LIGHT primary and accent (and their foregrounds) now
 * come from its brand-kit light combination (combinations.ts), as in
 * ./contract.ts. The other light fields and every dark field are unchanged.
 */

/**
 * Theme identifiers
 */
export type ThemeId =
  | 'default-canvas'
  | 'storm-confidence'
  | 'blue-momentum'
  | 'emerald-momentum'
  | 'sunshine-energy'
  | 'magenta-premium'
  | 'midnight-blueprint';

/**
 * #3071 S1: the ids before the switch to the brand-kit combination names. A browser may still hold
 * one in localStorage, and static pages (www-sbi, support-sbi, portfolio-sbi index.html, published
 * briefs) still set it as data-theme. theme.css styles both ids; normalizeThemeId reads either.
 */
export type LegacyThemeId = 'canvas' | 'storm' | 'blue' | 'emerald' | 'sunshine' | 'magenta';

/** #3071 S1: each legacy id and the id that replaced it. midnight-blueprint keeps its id. */
export const LEGACY_THEME_IDS: Readonly<Record<LegacyThemeId, ThemeId>> = {
  canvas: 'default-canvas',
  storm: 'storm-confidence',
  blue: 'blue-momentum',
  emerald: 'emerald-momentum',
  sunshine: 'sunshine-energy',
  magenta: 'magenta-premium',
};

/**
 * Theme color values for light and dark modes
 */
export interface ThemeColors {
  primary: string;
  primaryForeground: string;
  accent: string;
  accentForeground: string;
  background: string;
  foreground: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  muted: string;
  mutedForeground: string;
}

/**
 * Complete theme definition including metadata
 */
export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  description: string;
  emoji: string;
  applications: string[];
  light: ThemeColors;
  dark: ThemeColors;
}

/**
 * All 6 branded themes
 */
export const themes: Record<ThemeId, ThemeDefinition> = {
  'default-canvas': {
    id: 'default-canvas',
    name: 'Default Canvas',
    description: 'Clean white surfaces with Blue 400 primary and strategic brand accents.',
    emoji: '🎨',
    applications: [
      'Primary brand site',
      'Documentation',
      'Product interfaces',
      'Marketing landing pages',
    ],
    // Deployed canvas.light contract
    light: {
      primary: '#1c6ee9',
      primaryForeground: '#ffffff',
      accent: '#da2598',
      accentForeground: '#000000',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed canvas.dark contract
    dark: {
      primary: '#4a89f7',
      primaryForeground: '#1a1a1a', // #1170 Decision 1 fg-swap AA (was #ffffff, 5.15:1)
      accent: '#f9607d',
      accentForeground: '#1a1028', // #1170 Decision 1 fg-swap AA (was #ffffff, 6.09:1)
      background: '#1a1a1a',
      foreground: '#fafafa',
      surface: '#262626',
      surfaceAlt: '#303030',
      border: '#7d7d7d', // #1170 Decision 3 muted-nudge (was #737373; was #404040 pre-#1170)
      muted: '#303030',
      mutedForeground: '#b3b3b3',
    },
  },
  'storm-confidence': {
    id: 'storm-confidence',
    name: 'Storm Confidence',
    description: 'Sophisticated navy anchored by magenta pops for premium dashboards.',
    emoji: '💼',
    applications: [
      'Executive dashboards',
      'Premium client portals',
      'Investor reports',
      'High-touch onboarding',
    ],
    // Deployed storm.light contract (light neutral chrome + storm brand trio)
    light: {
      primary: '#002270',
      primaryForeground: '#ffffff',
      accent: '#da2598',
      accentForeground: '#000000',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed storm.dark contract (= midnight-blueprint)
    dark: {
      primary: '#4a89f7',
      primaryForeground: '#050b1f', // #1170 Decision 1 fg-swap AA (was #ffffff, 5.78:1)
      accent: '#ffdc44',
      accentForeground: '#050b1f',
      background: '#050b1f',
      foreground: '#e0e7f0',
      surface: '#13203a',
      surfaceAlt: '#1a2a47',
      border: '#5d79a8', // #1170 Decision 3 muted-nudge (was #516ea0; was #2a3a57 pre-#1170)
      muted: '#1a2a47',
      mutedForeground: '#9dabc0',
    },
  },
  'blue-momentum': {
    id: 'blue-momentum',
    name: 'Blue Momentum',
    description: 'Heroic blue foundation with sunshine highlights for confident launches.',
    emoji: '🚀',
    applications: [
      'Product marketing',
      'Acquisition funnels',
      'Data-heavy hero sections',
      'Sales enablement',
    ],
    // Deployed blue.light contract
    light: {
      primary: '#145ecc',
      primaryForeground: '#ffffff',
      accent: '#fdc10d',
      accentForeground: '#121212',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed blue.dark contract
    dark: {
      primary: '#1167f1',
      primaryForeground: '#ffffff',
      accent: '#ffe370',
      accentForeground: '#050b1f',
      background: '#050b1f',
      foreground: '#e0e7f0',
      surface: '#13203a',
      surfaceAlt: '#1a2a47',
      border: '#5d79a8', // #1170 Decision 3 muted-nudge (was #516ea0; was #2a3a57 pre-#1170)
      muted: '#1a2a47',
      mutedForeground: '#9dabc0',
    },
  },
  'emerald-momentum': {
    id: 'emerald-momentum',
    name: 'Emerald Momentum',
    description: 'Fresh greens grounded with blue accents for growth and sustainability.',
    emoji: '🌱',
    applications: [
      'Sustainability dashboards',
      'Health & wellness',
      'Financial wellbeing',
      'Environmental reports',
    ],
    // Deployed emerald.light contract
    light: {
      primary: '#27aa83',
      primaryForeground: '#121212',
      accent: '#1c6ee9',
      accentForeground: '#ffffff',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed emerald.dark contract
    dark: {
      primary: '#2ce887',
      primaryForeground: '#052016',
      accent: '#4a89f7',
      accentForeground: '#052016', // #1170 Decision 1 fg-swap AA (was #ffffff, 5.07:1)
      background: '#052016',
      foreground: '#d5f2e3',
      surface: '#133526',
      surfaceAlt: '#1a4432',
      border: '#509a6c', // #1170 Decision 3 muted-nudge (was #3a8a58; was #1f533b pre-#1170)
      muted: '#1a4432',
      mutedForeground: '#8ec9ad',
    },
  },
  'sunshine-energy': {
    id: 'sunshine-energy',
    name: 'Sunshine Energy',
    description: 'Optimistic yellows supported by deep storm typography.',
    emoji: '☀️',
    applications: [
      'Customer testimonials',
      'Onboarding journeys',
      'Educational tooltips',
      'Success stories',
    ],
    // Deployed sunshine.light contract
    light: {
      primary: '#fdc10d',
      primaryForeground: '#121212',
      accent: '#002270',
      accentForeground: '#ffffff',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed sunshine.dark contract
    dark: {
      primary: '#ffe370',
      primaryForeground: '#1a1028',
      accent: '#617394',
      accentForeground: '#ffffff',
      background: '#1a1028',
      foreground: '#f5e8ff',
      surface: '#261931',
      surfaceAlt: '#332240',
      border: '#9566a8', // #1170 Decision 3 muted-nudge (was #8a5a9e; was #3d2a4f pre-#1170)
      muted: '#332240',
      mutedForeground: '#c5a8d5',
    },
  },
  'magenta-premium': {
    id: 'magenta-premium',
    name: 'Magenta Premium',
    description: 'Bold magenta-led combination for premium features and exclusive offerings.',
    emoji: '💎',
    applications: [
      'Premium tier marketing',
      'VIP portals',
      'Exclusive features',
      'Milestone storytelling',
    ],
    // Deployed magenta.light contract
    light: {
      primary: '#da2598',
      primaryForeground: '#000000',
      accent: '#1c6ee9',
      accentForeground: '#ffffff',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Deployed magenta.dark contract
    dark: {
      primary: '#f9607d',
      primaryForeground: '#1a1028', // #1170 Decision 1 fg-swap AA (was #ffffff, 6.09:1)
      accent: '#ffdc44',
      accentForeground: '#1a1028',
      background: '#1a1028',
      foreground: '#f5e8ff',
      surface: '#261931',
      surfaceAlt: '#332240',
      border: '#9566a8', // #1170 Decision 3 muted-nudge (was #8a5a9e; was #3d2a4f pre-#1170)
      muted: '#332240',
      mutedForeground: '#c5a8d5',
    },
  },
  'midnight-blueprint': {
    id: 'midnight-blueprint',
    name: 'Midnight Blueprint',
    description:
      'Electric blues grounded by navy neutrals for analytics dashboards and data-rich interfaces.',
    emoji: '🌃',
    applications: [
      'Analytics dashboards',
      'Data visualization',
      'Night-mode navigation',
      'Developer tools',
    ],
    // Midnight Blueprint is a standalone dark theme; light mode uses the
    // deployed canvas.light contract (the data-theme='midnight-blueprint' block
    // is dark — see ./contract.ts). #3071 S2: kept equal to the new canvas light colors.
    light: {
      primary: '#1c6ee9',
      primaryForeground: '#ffffff',
      accent: '#da2598',
      accentForeground: '#000000',
      background: '#ffffff',
      foreground: '#121212',
      surface: '#ffffff',
      surfaceAlt: '#f8f8f8',
      border: '#e4e4e4', // #1170 Decision 2: decorative border reverted (form-field outline -> --input)
      muted: '#f3f1eb',
      mutedForeground: '#4f4f4f',
    },
    // Main identity of Midnight Blueprint = deployed midnight-blueprint / storm.dark
    dark: {
      primary: '#4a89f7', // Electric blue
      primaryForeground: '#050b1f', // #1170 Decision 1 fg-swap AA (was #ffffff, 5.78:1)
      accent: '#ffdc44', // Sunshine yellow accent
      accentForeground: '#050b1f',
      background: '#050b1f', // Deep midnight navy
      foreground: '#e0e7f0', // Light blue-gray
      surface: '#13203a', // Elevated navy
      surfaceAlt: '#1a2a47',
      border: '#5d79a8', // #1170 Decision 3 muted-nudge (was #516ea0; was #2a3a57 pre-#1170)
      muted: '#1a2a47',
      mutedForeground: '#9dabc0',
    },
  },
} as const;

/**
 * Get all theme IDs
 */
export const themeIds: ThemeId[] = [
  'default-canvas',
  'storm-confidence',
  'midnight-blueprint',
  'blue-momentum',
  'emerald-momentum',
  'sunshine-energy',
  'magenta-premium',
];

/**
 * Default theme
 */
export const defaultThemeId: ThemeId = 'storm-confidence';

/**
 * #3071 S1: a stored or attribute theme id as a current ThemeId: a current id as is, a legacy id
 * through LEGACY_THEME_IDS, anything else null. Pure.
 */
export function normalizeThemeId(id: string | null | undefined): ThemeId | null {
  if (!id) return null;
  if ((themeIds as readonly string[]).includes(id)) return id as ThemeId;
  return Object.prototype.hasOwnProperty.call(LEGACY_THEME_IDS, id)
    ? LEGACY_THEME_IDS[id as LegacyThemeId]
    : null;
}
